import { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  useToast,
  Text,
  VStack,
  InputGroup,
  InputLeftAddon,
  FormErrorMessage,
} from "@chakra-ui/react";
import { sendTestMessage } from "../../../api/admin/adminCampaign";
import { useAdminAuth } from "../../../contexts/AdminAuthContext";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  campaignId: string;
  messageText: string;
  socialShareImage?: string;
}

export const SendTestMessageModal = ({
  isOpen,
  onClose,
  campaignId,
  messageText,
  socialShareImage,
}: Props) => {
  const { adminToken } = useAdminAuth();
  const toast = useToast();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const cleaned = value.replace(/\D/g, "");

    // Format as (XXX) XXX-XXXX
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
  };

  const validatePhoneNumber = (value: string): boolean => {
    const cleaned = value.replace(/\D/g, "");
    return cleaned.length === 10;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    setError("");
  };

  const handleSend = async () => {
    if (!adminToken) return;

    const cleaned = phoneNumber.replace(/\D/g, "");

    if (!validatePhoneNumber(phoneNumber)) {
      setError("Please enter a valid 10-digit phone number");
      return;
    }

    setIsLoading(true);
    try {
      await sendTestMessage(adminToken, {
        campaignId,
        messageText,
        testPhoneNumber: cleaned,
        socialShareImage,
      });

      toast({
        title: "Test message sent! 📱",
        description: `Message sent to +1${cleaned}`,
        status: "success",
        duration: 3000,
      });

      setPhoneNumber("");
      onClose();
    } catch (err: any) {
      toast({
        title: "Failed to send test message",
        description: err.response?.data?.message || "Please try again",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPhoneNumber("");
    setError("");
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Send Test Message</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Send a test message to verify how it will appear to recipients.
              The message will include the campaign URL.
            </Text>

            <FormControl isInvalid={!!error}>
              <FormLabel>Phone Number</FormLabel>
              <InputGroup>
                <InputLeftAddon>+1</InputLeftAddon>
                <Input
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  maxLength={14}
                />
              </InputGroup>
              {error && <FormErrorMessage>{error}</FormErrorMessage>}
            </FormControl>

            <VStack
              align="stretch"
              p={3}
              bg="gray.50"
              borderRadius="md"
              spacing={2}
            >
              <Text fontSize="sm" fontWeight="600">
                Preview:
              </Text>
              <Text fontSize="sm" color="gray.700">
                {messageText}
              </Text>
              <Text fontSize="xs" color="blue.500">
                [shortened-url]
              </Text>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSend}
            isLoading={isLoading}
            isDisabled={!phoneNumber || phoneNumber.length < 14}
          >
            Send Test
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
